<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  SatButton,
  AtomRadio,
  WoLocation,
} from "@chatfood/design-system";
import { ref } from "vue";
import type { IOutletSelectionProps } from "./props";
import type { IDomainOutletState } from "#imports";
import { useDomainLabel } from "~/composables/use-domain-label";
import { formatDistance } from "~/utils/format-distance";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import { useLocale } from "~/composables/use-locale";
import type { IDomainOutletCoverage } from "~/domain/outlet-coverage";
import { OUTLET_STATES } from "~/domain/outlet-state";

const css = bemBuilder("outlet-selection");

const { outlets, isSelecting } = defineProps<IOutletSelectionProps>();

const emits = defineEmits<{
  select: [outlet: IDomainOutletCoverage];
}>();

const { outletStateMap } = useDomainLabel();

const { t } = useLocale();

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: OUTLET_STATES.OPEN,
  closed: OUTLET_STATES.CLOSED,
  busy: OUTLET_STATES.BUSY,
  pre_order_only: OUTLET_STATES.PRE_ORDER_ONLY,
};

const selectedOutlet = ref<IDomainOutletCoverage | null>(null);

const getStateDescription = (
  state: IDomainOutletState,
  deliveryTime: number,
) => {
  if (![OUTLET_STATES.OPEN, OUTLET_STATES.BUSY].includes(state)) return "";

  return t("components.outlet_selection.delivery_time", {
    deliveryStart: deliveryTime,
    deliveryEnd: deliveryTime + ETA_PREPARATION_MARGIN,
  });
};

const onOutletClick = (outlet: IDomainOutletCoverage) => {
  selectedOutlet.value = outlet;
};

const onConfirm = () => {
  if (selectedOutlet.value) {
    emits("select", selectedOutlet.value);
  }
};
</script>

<template>
  <div :class="css()">
    <div :class="css('header')">
      <AtomText type="h3">
        {{ t("components.outlet_selection.title") }}
      </AtomText>
      <AtomText type="body" color="color-text-secondary">
        {{ t("components.outlet_selection.subtitle") }}
      </AtomText>
    </div>

    <div :class="css('outlets')">
      <div v-for="outlet in outlets" :key="outlet.id" data-test="address-item">
        <AtomRadio
          :key="outlet.id"
          name="address-book"
          :checked="selectedOutlet?.id === outlet.id"
          :value="outlet.id"
          @change="onOutletClick(outlet)"
        >
          <WoLocation
            :address="outlet.address"
            :state="mapState[outlet.state]"
            :state-label="outletStateMap[outlet.state]"
            :address-prefix="
              formatDistance({
                distance: outlet.distance,
                translationConfig: { t },
              })
            "
            :state-description="
              getStateDescription(outlet.state, outlet.deliveryTime)
            "
            :name="outlet.name"
            :is-active="true"
            :warn-label="
              outlet.state === OUTLET_STATES.BUSY
                ? t('components.outlet_selection.busy_warning')
                : ''
            "
            :is-busy="outlet.state === OUTLET_STATES.BUSY"
            @click="onOutletClick(outlet)"
          />
        </AtomRadio>
      </div>
    </div>

    <SatButton
      size="large"
      theme="filled"
      variant="primary"
      data-test="apply-cta"
      :loading="isSelecting"
      :is-disabled="!selectedOutlet"
      @click="onConfirm"
    >
      {{ t("components.outlet_selection.confirm") }}
    </SatButton>
  </div>
</template>

<style lang="scss" scoped>
.outlet-selection {
  min-height: 360px;
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
  padding: var(--spacing-lg);

  @media (width >= $breakpoint-sm) {
    width: 450px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  &__outlets {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
    overflow-y: auto;
    max-height: 360px;
    padding: 1px;
    margin-bottom: var(--spacing-md);
  }

  :deep(.atom-radio) {
    align-items: flex-start;
  }

  :deep(.wo-location--is-active),
  :deep(.wo-location:hover) {
    box-shadow: none;
    padding: 0;
  }
}
</style>
